import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button as ButtonV2 } from "../../Button";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { InputV2 } from "../../InputV2";
import { Typo } from "../../Typo";
import { COMMENT_CHARACTER_LIMIT, SAVE_ERROR_MESSAGE } from "../consts";

type EditCommentModalProps = {
  subName: string;
  initial: string;
  onClose: () => void;
  /**
   * Design change #2 (PART-3930): may reject. A rejection keeps the modal open
   * with an inline error and commits nothing — closing on success is the
   * caller's job, which is what makes "no close on failure" fall out naturally.
   */
  onSave: (value: string) => void | Promise<void>;
};

export const EditCommentModal = ({
  subName,
  initial,
  onClose,
  onSave,
}: EditCommentModalProps) => {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  // Only mounted while open, so it holds the lock for its whole lifetime.
  useBodyScrollLock(true);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(value.trim());
    } catch {
      setError(SAVE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  // Re-homed from the old textarea onto InputV2's forwarded ref — the field
  // still takes focus on open.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(3,20,46,0.70)" }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="absolute left-1/2 top-1/2 flex w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit comment for ${subName}`}
      >
        <div className="flex items-center justify-between">
          <Typo element="h2" type="headingXL" textColor="text-text-primary">
            Comment
          </Typo>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-secondary hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        {/* Design change #1 (PART-3930): single-line InputV2 at 60 chars. The
            dialog's own "Comment" heading names the field, so it takes an
            ariaLabel rather than a second visible label. */}
        <div className="mt-4">
          <InputV2
            ref={inputRef}
            name="sub-ib-comment"
            value={value}
            characterLimit={COMMENT_CHARACTER_LIMIT}
            // Editing the text clears a failed-save error (design change #2).
            onChange={(next) => {
              setValue(next);
              setError("");
            }}
            error={error}
            placeholder="Add notes about this Sub IB. Only you can see this."
            ariaLabel={`Comment for ${subName}`}
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          {/* Cancel is disabled in flight (design change #2) so a pending save
              cannot be abandoned half-committed. */}
          <ButtonV2
            variant="tonal"
            size="md"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </ButtonV2>
          <ButtonV2
            variant="brand"
            size="md"
            isLoading={saving}
            onClick={handleSave}
          >
            Save
          </ButtonV2>
        </div>
      </div>
    </div>,
    document.body,
  );
};
